/**
 * ScreenshotAnnotatorComponent
 *
 * Full-screen dialog (opened via NgDialogAnimationService) for annotating a
 * screenshot with arrows, rectangles, highlights, freehand pen, and text.
 * Links the screenshot to an existing measurement via a typed dropdown.
 *
 * Drawing uses an inline SVG layer over an <img> background. Text annotations
 * are absolutely-positioned DOM `<input>` overlays. Annotations live in a
 * local working copy; only `save()` commits to ScreenshotsService.
 *
 * Form-control wiring: `annotatorForm` (caption + linkedMeasurementId) drives
 * the HTML; mock data is patched in `ngOnInit` from MAT_DIALOG_DATA.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { trigger, transition, style, animate } from '@angular/animations';

import {
  Annotation,
  AnnotationKind,
  LinkableMeasurement,
  Screenshot,
  ScreenshotAnnotatorData,
} from '../screenshots.types';
import {
  ANNOTATION_COLORS,
  ANNOTATOR_TOOLS,
  AnnotatorTool,
  DEFAULT_STROKE_WIDTH,
} from '../screenshots.constants';
import { ScreenshotsService } from '../screenshots.service';

type AnnotatorToolKind = AnnotationKind | 'select';

interface AnnotatorFormControls {
  caption: FormControl<string>;
  linkedMeasurementId: FormControl<string | null>;
}

@Component({
  standalone: false,
  selector: 'app-screenshot-annotator',
  templateUrl: './screenshot-annotator.component.html',
  styleUrls: ['./screenshot-annotator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('zoomIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.96)' }),
        animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ],
})
export class ScreenshotAnnotatorComponent implements OnInit {
  public readonly screenshot: Screenshot;
  public readonly linkableMeasurements: LinkableMeasurement[];

  // Reactive form for caption + measurement link (mock data → form control → HTML)
  public annotatorForm = new FormGroup<AnnotatorFormControls>({
    caption: new FormControl<string>('', { nonNullable: true }),
    linkedMeasurementId: new FormControl<string | null>(null),
  });

  // Drawing state
  public tool: AnnotatorToolKind = 'select';
  public activeColor: string = ANNOTATION_COLORS[0];
  public annotations: Annotation[] = [];
  public readonly tools = ANNOTATOR_TOOLS;
  public readonly colors = ANNOTATION_COLORS;

  // Working draft annotation during a drag
  public draft: Annotation | null = null;
  private isDrawing = false;
  /** Undo/redo history snapshots (annotation arrays). */
  private history: Annotation[][] = [];
  private redoStack: Annotation[][] = [];

  @ViewChild('canvasArea', { static: false }) canvasAreaRef?: ElementRef<HTMLDivElement>;

  constructor(
    public readonly dialogRef: MatDialogRef<ScreenshotAnnotatorComponent, Partial<Screenshot> | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: ScreenshotAnnotatorData,
    private readonly screenshotsService: ScreenshotsService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.screenshot = data.screenshot;
    this.linkableMeasurements = data.linkableMeasurements ?? [];
  }

  ngOnInit(): void {
    // Patch the mock/existing data into reactive form controls — never bind raw.
    this.annotatorForm.patchValue({
      caption: this.screenshot.caption ?? '',
      linkedMeasurementId: this.screenshot.linkedMeasurementId ?? null,
    });
    // Deep-clone annotations so undo works without mutating source.
    this.annotations = this.screenshot.annotations.map((a) => ({ ...a, points: a.points ? [...a.points] : undefined }));
  }

  // ===== Toolbar handlers =====

  setTool(kind: AnnotatorToolKind): void {
    this.tool = kind;
  }

  setColor(color: string): void {
    this.activeColor = color;
  }

  undo(): void {
    if (this.history.length === 0) return;
    const previous = this.history.pop()!;
    this.redoStack.push(this.annotations);
    this.annotations = previous;
    this.cdr.markForCheck();
  }

  clear(): void {
    if (this.annotations.length === 0) return;
    this.snapshotForUndo();
    this.annotations = [];
  }

  deleteLast(): void {
    if (this.annotations.length === 0) return;
    this.snapshotForUndo();
    this.annotations = this.annotations.slice(0, -1);
  }

  /** Commit changes via the service, then close. */
  save(): void {
    const patch: Partial<Screenshot> = {
      caption: this.annotatorForm.controls.caption.value,
      annotations: this.annotations,
    };
    const linkedId = this.annotatorForm.controls.linkedMeasurementId.value;
    if (linkedId) {
      const link = this.linkableMeasurements.find((m) => m.id === linkedId);
      if (link) {
        patch.linkedMeasurementId = link.id;
        patch.linkedMeasurementName = link.name;
        patch.linkedMeasurementColor = link.color;
      }
    } else {
      // Linked measurement cleared
      patch.linkedMeasurementId = undefined;
      patch.linkedMeasurementName = undefined;
      patch.linkedMeasurementColor = undefined;
    }
    this.screenshotsService.updateScreenshot(this.screenshot.id, patch);
    this.dialogRef.close(patch);
  }

  close(): void {
    this.dialogRef.close(null);
  }

  jumpToSource(): void {
    // Closes the dialog with a signal for the host to call onScreenshotLocate.
    // The host can also call locate directly from the panel/toast handlers.
    this.dialogRef.close({ __locate: true } as any);
  }

  /** Triggered by the in-annotator Share button — close with a share signal. */
  requestShare(): void {
    this.dialogRef.close({ __share: true } as any);
  }

  // ===== SVG drawing handlers =====

  onSvgMouseDown(event: MouseEvent): void {
    if (this.tool === 'select') return;
    const p = this.toLocal(event);
    this.isDrawing = true;
    this.snapshotForUndo();

    if (this.tool === 'text') {
      // Click-to-place text — finalize immediately as a draft for inline editing.
      this.draft = this.createAnnotation('text', p.x, p.y);
      this.annotations = [...this.annotations, this.draft];
      this.draft = null;
      this.isDrawing = false;
      this.cdr.markForCheck();
      return;
    }

    if (this.tool === 'pen') {
      this.draft = { ...this.createAnnotation('pen', p.x, p.y), points: [{ x: p.x, y: p.y }] };
    } else {
      this.draft = this.createAnnotation(this.tool, p.x, p.y);
    }
    this.cdr.markForCheck();
  }

  onSvgMouseMove(event: MouseEvent): void {
    if (!this.isDrawing || !this.draft) return;
    const p = this.toLocal(event);
    switch (this.draft.kind) {
      case 'arrow':
        this.draft = { ...this.draft, endX: p.x, endY: p.y };
        break;
      case 'rect':
      case 'highlight':
        this.draft = {
          ...this.draft,
          width: p.x - this.draft.x,
          height: p.y - this.draft.y,
        };
        break;
      case 'pen':
        this.draft = { ...this.draft, points: [...(this.draft.points ?? []), { x: p.x, y: p.y }] };
        break;
    }
    this.cdr.markForCheck();
  }

  onSvgMouseUp(_event: MouseEvent): void {
    if (!this.isDrawing || !this.draft) {
      this.isDrawing = false;
      return;
    }
    // Normalize rect/highlight so width/height are positive.
    if (this.draft.kind === 'rect' || this.draft.kind === 'highlight') {
      const { x, y, width = 0, height = 0 } = this.draft;
      this.draft = {
        ...this.draft,
        x: width < 0 ? x + width : x,
        y: height < 0 ? y + height : y,
        width: Math.abs(width),
        height: Math.abs(height),
      };
    }
    this.annotations = [...this.annotations, this.draft];
    this.draft = null;
    this.isDrawing = false;
    this.cdr.markForCheck();
  }

  onTextInputChange(annotation: Annotation, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const next = { ...annotation, text: value };
    this.annotations = this.annotations.map((a) => (a.id === annotation.id ? next : a));
  }

  // ===== Keyboard shortcuts =====

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.isInputFocused(event.target)) {
      if (event.key === 'Escape') (event.target as HTMLElement).blur();
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      if (event.key.toLowerCase() === 'z') {
        this.undo();
        event.preventDefault();
        return;
      }
      if (event.key.toLowerCase() === 's') {
        this.save();
        event.preventDefault();
        return;
      }
    }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const key = event.key.toLowerCase();
    if (key === 'escape') {
      this.close();
      return;
    }
    const tool = this.tools.find((t) => t.shortcut === key);
    if (tool) {
      this.setTool(tool.id);
      event.preventDefault();
    }
  }

  // ===== SVG geometry helpers =====

  /** Arrow head as an SVG polygon string. */
  arrowHeadPath(a: Annotation): string {
    const headLen = 12;
    const ex = a.endX ?? a.x;
    const ey = a.endY ?? a.y;
    const dx = ex - a.x;
    const dy = ey - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    // Perpendicular vector
    const px = -uy;
    const py = ux;
    const x1 = ex - ux * headLen + px * (headLen * 0.5);
    const y1 = ey - uy * headLen + py * (headLen * 0.5);
    const x2 = ex - ux * headLen - px * (headLen * 0.5);
    const y2 = ey - uy * headLen - py * (headLen * 0.5);
    return `${ex},${ey} ${x1},${y1} ${x2},${y2}`;
  }

  /** Pen path as an SVG path `d` string. */
  penPath(a: Annotation): string {
    const pts = a.points ?? [];
    if (pts.length === 0) return '';
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  }

  /** All annotations + the in-progress draft, in render order. */
  get renderList(): Annotation[] {
    return this.draft ? [...this.annotations, this.draft] : this.annotations;
  }

  trackByAnnotation(_: number, a: Annotation): string {
    return a.id;
  }

  // ===== Internals =====

  private toLocal(event: MouseEvent): { x: number; y: number } {
    const rect = this.canvasAreaRef?.nativeElement.getBoundingClientRect();
    if (!rect) return { x: event.clientX, y: event.clientY };
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private createAnnotation(kind: AnnotationKind, x: number, y: number): Annotation {
    const id = `a-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
    return {
      id,
      kind,
      color: this.activeColor,
      x,
      y,
      strokeWidth: DEFAULT_STROKE_WIDTH[kind],
      ...(kind === 'arrow' ? { endX: x, endY: y } : {}),
      ...(kind === 'rect' || kind === 'highlight' ? { width: 0, height: 0 } : {}),
      ...(kind === 'text' ? { text: '' } : {}),
      ...(kind === 'pen' ? { points: [] } : {}),
    };
  }

  private snapshotForUndo(): void {
    this.history.push(this.annotations);
    this.redoStack = [];
    if (this.history.length > 50) this.history.shift();
  }

  private isInputFocused(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    const tag = target.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || (target as HTMLElement).isContentEditable;
  }
}
